/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 建站舱 · 站点 API(契约:docs/plans/2026-09-22-建站舱-实施计划-console.md 顶部「API 契约」块,
 * 与 api 计划的 SiteDtos / CollectDtos / ContentDtos / PublishDtos / DomainDtos 同源)
 * ═══════════════════════════════════════════════════════════════════════════
 * ★ 字段名逐字照抄契约的 camelCase,**本仓不做映射层**。改字段名必须先改契约文件。
 * ★ 后端(gscq-api)此刻在另一个 worktree 并行开发:dev 模式下**只读口**失败退到
 *   `./websiteFixtures` 的青山科技样例(**只在 `import.meta.env.MODE === "development"`**,
 *   vitest 的 MODE 是 `test`、构建产物是 `production`,两处都拿不到桩),并置 fixture 旗子
 *   让壳挂「样例数据」横幅。端点一通,桩自动让位。
 * ★★ 写口(POST / PUT / DELETE)**一律不兜底,直接抛**:假装建站 / 发布 / 绑域名成功,
 *   比一个报错更糟 —— 那是账和状态都是编的。
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { api } from "@/shared/api";
import { logger } from "@/shared/lib/logger";

import type {
  ActivityItem,
  BindDomainRequest,
  Checklist,
  CollectAnswer,
  CreateSiteRequest,
  DomainCard,
  DomainOverview,
  DraftResult,
  GenerateDraftRequest,
  HostSlots,
  PreflightResult,
  RewriteRequest,
  SiteCard,
  SiteContent,
  SiteDetail,
  SiteVersion,
  Template,
  TemplateSummary,
  UploadImageRequest,
  UploadImageResult,
} from "./types";

// 契约类型单源在 ./types;这里原样再导出,调用方照旧可以从 websiteService 拿类型。
export * from "./types";

const BASE = "/website";

// ── 错误(契约:422 CONFIG_INCOMPLETE 带 details 整批;403 MODULE_NOT_ENABLED;409 CONFLICT)──

export const CONFIG_INCOMPLETE = "CONFIG_INCOMPLETE";

/**
 * 422 CONFIG_INCOMPLETE 的 `details` 是**整批**错误(缺哪些必填事实 / site.json 哪几处不合规),
 * 页面逐条展示,不要只亮第一条(0830 教训:逐字段 422 修掉第一个只揭开下一个)。
 * ★ 鸭子判型不 `instanceof ApiError`:测试里 `@/shared/api` 整个被桩掉,类不存在。
 * @returns `null` = 不是 CONFIG_INCOMPLETE;`[]` = 是,但后端没给逐条 details,页面退回显示 `e.message`。
 */
export function configIncompleteDetails(e: unknown): string[] | null {
  const err = e as { errorCode?: unknown; details?: unknown } | null | undefined;
  if (!err || err.errorCode !== CONFIG_INCOMPLETE) return null;
  return Array.isArray(err.details)
    ? err.details.filter((d): d is string => typeof d === "string")
    : [];
}

export const DRAFT_INVALID = "DRAFT_INVALID";

/** api `WebsiteDraftService.DRAFT_INVALID` 的原话:初稿(ai 产出)没过 site.json 校验时 422 的 message。 */
const DRAFT_INVALID_MESSAGE = "初稿没有通过校验";

/** 校验器一条错误的 `details` 形状:`{path}: {message}`(api `ValidationError.toString()`)。缺必填事实那条是 label,不长这样。 */
const VALIDATOR_DETAIL = /^[A-Za-z_][\w.[\]]*: \S/;

/**
 * 出稿 422 是不是「ai 出的初稿没过校验」(而不是「资料还不够」):错误码 `DRAFT_INVALID`;或过渡期的
 * `CONFIG_INCOMPLETE` + api 那句原话;或 details 全是校验器的 `path: message` 形状。
 * ★ 两种 422 同码不同义:一个要用户回去补资料,一个是 AI 那边出错、重新生成就行 —— 标题不能说成一回事。
 */
export function isDraftInvalid(e: unknown): boolean {
  const err = e as { errorCode?: unknown; message?: unknown } | null | undefined;
  if (err?.errorCode === DRAFT_INVALID) return true;
  const details = configIncompleteDetails(e);
  if (details === null) return false;
  if (err?.message === DRAFT_INVALID_MESSAGE) return true;
  return details.length > 0 && details.every((d) => VALIDATOR_DETAIL.test(d));
}

/**
 * 发布 / 回滚的 409「结果未知」(api `HttpPublishGateway.OUTCOME_UNKNOWN`:「发布服务器响应超时,结果未知,请稍后刷新版本列表」)。
 * 请求已经送到发布服务器、回应没等到 —— 对面**可能已经发布了**;api 把这一版留在 `pending`(过期规则收尾),既不标失败也不标成功。
 * ★ 页面不能把它画成红色「发布失败」(用户会再点一次,撞上「有另一次发布正在进行」),要说「结果未知」并重拉版本 / 站点。
 * 鸭子判型,原因同 configIncompleteDetails。
 */
export function isPublishOutcomeUnknown(e: unknown): boolean {
  const err = e as { httpStatus?: unknown; message?: unknown } | null | undefined;
  return (
    err?.httpStatus === 409 && typeof err.message === "string" && err.message.includes("结果未知")
  );
}

/** 422 的 details(不管是哪个码);没有就空数组。 */
export function errorDetails(e: unknown): string[] {
  const d = (e as { details?: unknown } | null | undefined)?.details;
  return Array.isArray(d) ? d.filter((x): x is string => typeof x === "string") : [];
}

// ── dev 桩:后端没上线时让页面跑起来 ────────────────────────────────────────
//
// ★ 判据用 MODE 不用 DEV:vitest 下 `import.meta.env.DEV` 也是 true,那样组件测会
//   悄悄吃到桩数据 —— 失败形态是静默的(测试永远绿,因为它测的是桩)。
const FIXTURES_ON = import.meta.env.MODE === "development";

/**
 * ★★ 回落到桩的那一刻必须**在页面上说出来**(0916 纪律:fixture 开关 + 明标「样例数据」,
 *   不做静默回落)。旗子只会从 false → true,刷新页面即清 —— 不做「某口成功了就撤旗」,
 *   那会让横幅忽隐忽现、更像是页面自己有毛病。
 */
let fixtureUsed = false;
const fixtureListeners = new Set<() => void>();

export function isWebsiteFixtureData(): boolean {
  return fixtureUsed;
}

export function subscribeWebsiteFixtureFlag(fn: () => void): () => void {
  fixtureListeners.add(fn);
  return () => {
    fixtureListeners.delete(fn);
  };
}

/** 测试 / 走查用:把旗子放倒。 */
export function __resetWebsiteFixtureFlag() {
  fixtureUsed = false;
  fixtureListeners.forEach((fn) => fn());
}

function markFixtureUsed() {
  if (fixtureUsed) return;
  fixtureUsed = true;
  fixtureListeners.forEach((fn) => fn());
}

/** 测试用(与 `__resetWebsiteFixtureFlag` 对称):把旗子竖起来,量「横幅真的会出现」这条正向断言。 */
export function __markWebsiteFixtureUsed() {
  markFixtureUsed();
}

type WebsiteFixtures = typeof import("./websiteFixtures");

/**
 * 后端「在,且明确回了话」的错误:带数字 `httpStatus` 且 < 500(401 / 403 模块未开通 / 404 / 422)。
 * ★ 这类不回桩 —— 回了,就是把一个真 403「模块未开通」画成一屏样例站点。只有网络错误
 *   (没有 httpStatus)与 5xx 才算「后端不在」。鸭子判型,原因同 configIncompleteDetails。
 */
function isClientError(e: unknown): boolean {
  const status = (e as { httpStatus?: unknown } | null | undefined)?.httpStatus;
  return typeof status === "number" && status < 500;
}

/** 只读口用:失败且 dev 模式且是「后端不在」那一类 → 桩;否则原样抛。 */
async function withFixture<T>(run: () => Promise<T>, pick: (m: WebsiteFixtures) => T): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (!FIXTURES_ON || isClientError(e)) throw e;
    const mod = await import("./websiteFixtures");
    logger.warn("[website] 后端未就绪,本次走 dev 本地桩", { error: String(e) });
    markFixtureUsed();
    return pick(mod);
  }
}

const site = (id: string) => `${BASE}/sites/${encodeURIComponent(id)}`;

export const websiteService = {
  // ── 模板 ──
  listTemplates: () =>
    withFixture(
      () => api.get<TemplateSummary[]>(`${BASE}/templates`),
      (m) => m.fixtureTemplates
    ),
  getTemplate: (code: string) =>
    withFixture(
      () => api.get<Template>(`${BASE}/templates/${encodeURIComponent(code)}`),
      (m) => m.fixtureTemplate(code)
    ),

  // ── 站点 ──
  listSites: () =>
    withFixture(
      () => api.get<SiteCard[]>(`${BASE}/sites`),
      (m) => m.fixtureSites
    ),
  /** 写口:不回桩。 */
  createSite: (body: CreateSiteRequest) => api.post<SiteDetail>(`${BASE}/sites`, body),
  getSite: (id: string) =>
    withFixture(
      () => api.get<SiteDetail>(site(id)),
      (m) => m.fixtureSite(id)
    ),
  /** 写口:不回桩(ADMIN / MANAGER)。 */
  deleteSite: (id: string) => api.delete<void>(site(id)),

  // ── 资料采集 ──
  /** 写口(跑 ai 预填,有成本):不回桩。回包就是合并后的清单,带 `warnings`(GET collect 不带)。 */
  prefill: (id: string) => api.post<Checklist>(`${site(id)}/collect/prefill`, undefined),
  checklist: (id: string) =>
    withFixture(
      () => api.get<Checklist>(`${site(id)}/collect`),
      (m) => m.fixtureChecklist
    ),
  /** 写口:不回桩。 */
  answer: (id: string, answers: CollectAnswer[]) =>
    api.put<Checklist>(`${site(id)}/collect/answers`, { answers }),

  // ── 草稿 / 内容 ──
  /**
   * 写口(出稿扣费):不回桩;缺必填事实且不许占位 → 422 CONFIG_INCOMPLETE(details 整批)。
   * 回包 `{content, warnings}`(附录 A.34),不是裸 SiteContent。
   */
  generateDraft: (id: string, body: GenerateDraftRequest) =>
    api.post<DraftResult>(`${site(id)}/draft/generate`, body),
  getContent: (id: string) =>
    withFixture(
      () => api.get<SiteContent>(`${site(id)}/content`),
      (m) => m.fixtureContentFor(id)
    ),
  /** 写口:不回桩;整份校验,422 带全部错误。 */
  saveContent: (id: string, content: SiteContent) =>
    api.put<SiteContent>(`${site(id)}/content`, content),
  /**
   * 写口:不回桩;事实字段 → 403「事实字段不能由 AI 改写」。请求体带框里的当前值 `current`(附录 A.36);
   * host 模式送 `token` 不送 `path`(A.55)。
   */
  rewrite: (id: string, body: RewriteRequest) =>
    api.post<{ value: string }>(`${site(id)}/content/rewrite`, body),

  // ── 图片(A.53,host 模式)──
  /** 写口:不回桩。回 `{url}`(宿主相对或完整 url),页面补成完整 url 再写进 site.json。 */
  uploadImage: (id: string, body: UploadImageRequest) =>
    api.post<UploadImageResult>(`${site(id)}/assets/image`, body),
  /** 1009:模板全部图片位 / 调色板 / 导航与按钮可改文字(只读)。 */
  hostSlots: (id: string) =>
    withFixture(
      () => api.get<HostSlots>(`${site(id)}/host-slots`),
      (m) => m.fixtureHostSlots
    ),

  // ── 发布 / 版本 ──
  /** 只读体检(A.50):errors 非空 = 发布会被 422 挡;warnings = 会被清空的模板图片 / 外链。 */
  preflight: (id: string) =>
    withFixture(
      () => api.get<PreflightResult>(`${site(id)}/publish/preflight`),
      (m) => m.fixturePreflight
    ),
  /**
   * 写口:不回桩。409 CONFLICT 几种原话:「发布失败:…」(网关明确失败,版本记 failed)/「有另一次发布正在进行」/
   * 「…结果未知,请稍后刷新版本列表」(版本留 pending,见 {@link isPublishOutcomeUnknown})/「…已被后来的一次发布接替,请刷新版本列表」。
   */
  publish: (id: string) => api.post<SiteVersion>(`${site(id)}/publish`, undefined),
  listVersions: (id: string) =>
    withFixture(
      () => api.get<SiteVersion[]>(`${site(id)}/versions`),
      (m) => m.fixtureVersionsFor(id)
    ),
  /** 写口:不回桩;回滚走与发布同一条网关路,409 的几种原话同 `publish`。 */
  rollback: (id: string, versionId: string) =>
    api.post<SiteVersion>(
      `${site(id)}/versions/${encodeURIComponent(versionId)}/rollback`,
      undefined
    ),

  // ── 域名 ──
  domains: (id: string) =>
    withFixture(
      () => api.get<DomainOverview>(`${site(id)}/domains`),
      (m) => m.fixtureDomainsFor(id)
    ),
  /** 写口:不回桩。 */
  bindDomain: (id: string, body: BindDomainRequest) =>
    api.post<DomainCard>(`${site(id)}/domains`, body),
  /** 写口(真 DNS 查询,30s 频控):不回桩。 */
  checkDomain: (id: string, domainId: string) =>
    api.post<DomainCard>(`${site(id)}/domains/${encodeURIComponent(domainId)}/check`, undefined),
  /** 写口:不回桩(ADMIN / MANAGER)。 */
  unbindDomain: (id: string, domainId: string) =>
    api.delete<void>(`${site(id)}/domains/${encodeURIComponent(domainId)}`),

  // ── 动态 ──
  activity: (id: string) =>
    withFixture(
      () => api.get<ActivityItem[]>(`${site(id)}/activity`),
      (m) => m.fixtureActivity
    ),
};

export type WebsiteService = typeof websiteService;
